'use client';

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Search, ChevronDown, Check, Loader2, X } from 'lucide-react';
import { uiViewportHeight } from '@/lib/uiScale';

export interface AsyncSearchableSelectOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

interface AsyncSearchableSelectProps {
  /** Currently selected value (empty string = nothing selected) */
  value: string;
  /** Called when user selects an option */
  onChange: (value: string) => void;
  /** Async search callback — receives search term, returns options */
  onSearch: (term: string) => Promise<AsyncSearchableSelectOption[]>;
  /** Placeholder for the trigger button when nothing is selected */
  placeholder?: string;
  /** Placeholder for the search input inside the dropdown */
  searchPlaceholder?: string;
  /** Message shown when search returns no results */
  emptyMessage?: string;
  /** Message shown when no search has been performed yet */
  idleMessage?: string;
  /** When true, calls onSearch('') when the dropdown opens to load initial results */
  loadOnOpen?: boolean;
  disabled?: boolean;
  isDark?: boolean;
  className?: string;
  /** Unique id prefix for aria attributes */
  idPrefix?: string;
  /** Debounce delay in ms (default 300) */
  debounceMs?: number;
  /** Optional: display label for the currently selected value (overrides option.label) */
  selectedDisplay?: string;
  /** Optional: icon for the currently selected value */
  selectedIcon?: React.ReactNode;
}

export function AsyncSearchableSelect({
  value,
  onChange,
  onSearch,
  placeholder = 'Search...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found',
  idleMessage = 'Start typing to search...',
  loadOnOpen = false,
  disabled = false,
  isDark = false,
  className = '',
  idPrefix = 'async-searchable-select',
  debounceMs = 300,
  selectedDisplay,
  selectedIcon,
}: AsyncSearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<AsyncSearchableSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  // Keep the latest onSearch in a ref so the debounced effect doesn't
  // re-run when the parent passes a new inline callback reference.
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value],
  );

  const displayLabel = selectedDisplay ?? selectedOption?.label ?? '';
  const displayIcon = selectedIcon ?? selectedOption?.icon;

  const handleSelect = useCallback(
    (val: string) => {
      onChange(val);
      setOpen(false);
      setQuery('');
      setOptions([]);
      setHighlightedIndex(0);
    },
    [onChange],
  );

  // Debounced async search
  useEffect(() => {
    if (!open) return;

    // When loadOnOpen is false and query is empty, clear results
    if (!query.trim() && !loadOnOpen) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const currentId = ++requestIdRef.current;
    const searchTerm = query.trim();
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await onSearchRef.current(searchTerm);
        // Only apply if this is still the latest request
        if (currentId === requestIdRef.current) {
          setOptions(results);
          setHighlightedIndex(0);
        }
      } catch (err) {
        console.error('[AsyncSearchableSelect] Search failed:', err);
        if (currentId === requestIdRef.current) {
          setOptions([]);
        }
      } finally {
        if (currentId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, debounceMs, loadOnOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[highlightedIndex];
      if (opt) handleSelect(opt.value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
      setOptions([]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
        setOptions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const handleToggle = () => {
    if (!open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = uiViewportHeight() - rect.bottom;
      const dropdownHeight = 300;
      setDropUp(spaceBelow < dropdownHeight && rect.top > dropdownHeight);
    }
    setOpen(!open);
  };

  const baseInputClass = `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60 ${
    isDark
      ? 'border-gray-600 bg-gray-900 text-gray-100'
      : 'border-gray-300 bg-white text-gray-900'
  } ${className}`;

  const hasSelection = !!value;

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      {/* Trigger button / selected display */}
      {hasSelection ? (
        <div className="flex items-center gap-2">
          <div className={`${baseInputClass} flex items-center gap-2`}>
            {displayIcon && <span className="flex-shrink-0">{displayIcon}</span>}
            <span className="truncate flex-1">{displayLabel}</span>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOptions([]);
              }}
              className={`rounded p-1 ${isDark ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-100'}`}
              title="Clear selection"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={handleToggle}
          className={`${baseInputClass} flex items-center justify-between text-left ${
            !value ? (isDark ? 'text-gray-500' : 'text-gray-400') : ''
          }`}
          aria-expanded={open}
          aria-controls={`${idPrefix}-results`}
        >
          <span className="flex items-center gap-2 truncate">
            {displayIcon && <span className="flex-shrink-0">{displayIcon}</span>}
            <span className="truncate">{placeholder}</span>
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${
              isDark ? 'text-gray-400' : 'text-gray-400'
            }`}
          />
        </button>
      )}

      {/* Dropdown */}
      {open && !hasSelection && (
        <div
          className={`absolute z-50 w-full rounded-lg border shadow-lg max-h-72 overflow-hidden flex flex-col ${
            dropUp ? 'bottom-full mb-1' : 'mt-1'
          } ${
            isDark ? 'border-gray-600 bg-gray-900' : 'border-gray-200 bg-white'
          }`}
        >
          {/* Search input */}
          <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <div className="relative">
              <Search
                className={`absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${
                  isDark ? 'text-gray-500' : 'text-gray-400'
                }`}
              />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className={`w-full rounded-md border pl-7 pr-7 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  isDark
                    ? 'border-gray-600 bg-gray-800 text-gray-100'
                    : 'border-gray-200 bg-white text-gray-900'
                }`}
                role="combobox"
                aria-expanded={open}
                aria-controls={`${idPrefix}-results`}
              />
              {loading && (
                <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-gray-400" />
              )}
            </div>
          </div>

          {/* Options list */}
          <div id={`${idPrefix}-results`} role="listbox" className="overflow-y-auto flex-1">
            {!query.trim() && !loadOnOpen ? (
              <div className={`p-3 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {idleMessage}
              </div>
            ) : loading ? (
              <div className={`p-3 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Searching...
              </div>
            ) : options.length === 0 ? (
              <div className={`p-3 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {emptyMessage}
              </div>
            ) : (
              options.map((opt, index) => {
                const isSelected = opt.value === value;
                const isHighlighted = index === highlightedIndex;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSelect(opt.value)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    role="option"
                    aria-selected={isSelected}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors ${
                      isHighlighted
                        ? isDark
                          ? 'bg-gray-800'
                          : 'bg-blue-50'
                        : isDark
                          ? 'hover:bg-gray-800'
                          : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center gap-2 truncate min-w-0">
                      {opt.icon && <span className="flex-shrink-0">{opt.icon}</span>}
                      <span className="flex-1 min-w-0">
                        <span className={`block truncate font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          {opt.label}
                        </span>
                        {opt.description && (
                          <span className={`block truncate text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {opt.description}
                          </span>
                        )}
                      </span>
                    </span>
                    {isSelected && <Check className="h-3.5 w-3.5 text-blue-500 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
